import { z } from "zod";
import { capabilitySchema } from "./capabilities";
import { studyMemberRoleSchema } from "./studyAccess";
import { StudyVisibilitySchema } from "./studies";
import { professionTypeSchema } from "./utils";

/**
 * A study's PEOPLE: who belongs to it, in what role, holding what.
 *
 * This replaced a `Study.createdByUserId` column doing three jobs at once —
 * naming who may hand out access, who may open the study to the facility, and
 * who acts through their own profession. One immutable user cannot be
 * transferred, cannot be two people, and leaves a study orphaned the moment
 * they go. A membership row does all three and survives its holder.
 *
 * The creator is simply the first OWNER. Everything else follows: a
 * co-investigator is a second OWNER, a handover is a role change, and a study
 * always keeps at least one active OWNER because the API refuses to remove or
 * demote the last one.
 */
export const studyMemberSchema = z.object({
  userId: z.string(),
  role: studyMemberRoleSchema,
  capabilities: z.array(capabilitySchema),
  createdAt: z.coerce.date(),
  user: z.object({
    id: z.string(),
    firstName: z.string(),
    lastName: z.string(),
    professionType: professionTypeSchema,
  }),
});
export type StudyMember = z.infer<typeof studyMemberSchema>;

export const studyMembersParamsSchema = z.object({ studyId: z.string() });
export type StudyMembersParams = z.infer<typeof studyMembersParamsSchema>;

export const studyMemberParamsSchema = z.object({
  studyId: z.string(),
  userId: z.string(),
});
export type StudyMemberParams = z.infer<typeof studyMemberParamsSchema>;

export const listStudyMembersSchemas = {
  params: studyMembersParamsSchema,
  response: {
    200: z.object({
      members: z.array(studyMemberSchema),
      /**
       * What the CALLER may hand out here — what they hold themselves, since
       * you cannot grant what you do not have. The client renders exactly
       * these boxes rather than deciding for itself which to grey out.
       */
      assignable: z.array(capabilitySchema),
    }),
  },
};

/**
 * Add someone, or restate what an existing member holds: REPLACE semantics,
 * so a second call is an edit rather than an accumulation and the body always
 * says the full desired state.
 *
 * `VIEW_STUDY` is added server-side, so a read-only invitation is an empty
 * list or that capability alone. Refusals worth knowing: assigning something
 * the caller does not hold is 403, and demoting or removing the last OWNER is
 * 409 `STUDY_LAST_OWNER` — the invariant that keeps a study from orphaning.
 */
export const upsertStudyMemberSchemas = {
  params: studyMembersParamsSchema,
  body: z.object({
    userId: z.string(),
    role: studyMemberRoleSchema.default("MEMBER"),
    capabilities: z.array(capabilitySchema),
  }),
  response: { 201: studyMemberSchema },
};
export type UpsertStudyMemberBody = z.infer<
  typeof upsertStudyMemberSchemas.body
>;

export const removeStudyMemberSchemas = {
  params: studyMemberParamsSchema,
  response: { 204: z.void() },
};

/**
 * Opening a study to the whole facility, or closing it back to its members.
 *
 * It lives with the guest list rather than on `PATCH /studies/:id` for two
 * reasons: it is a decision about the AUDIENCE and not about the protocol, and
 * the study edit is DRAFT-only — a running study is exactly the one whose
 * audience someone might need to widen.
 */
export const setStudyVisibilitySchemas = {
  params: studyMembersParamsSchema,
  body: z.object({ visibility: StudyVisibilitySchema }),
  response: { 200: z.object({ visibility: StudyVisibilitySchema }) },
};
export type SetStudyVisibilityBody = z.infer<
  typeof setStudyVisibilitySchemas.body
>;
